import {Injectable} from '@angular/core';
import {LensCatalogApiService} from './lens-catalog-api.service';
import {ReferenceListItem} from './data/reference-list-item';
import {
  LensCatalogCoatingReferenceType,
  LensMaterialReferenceType,
  LensCatalogReferenceType,
  LCatRefWithOrderNumberType,
  LensCoatingTypeResponse,
  LensMaterialTypeResponse,
  LensVisionTypeResponse
} from './data/lens-catalog-api-item';
import {Coating} from './data/coating';
import {LensFeature} from './data/lens-feature';
import {LensFeatureType} from '../shared/data/lens-feature-type';
import {LabOrder} from './data/lab-order';
import {Observable} from 'rxjs/Observable';
import {LensStyle} from './data/lensstyle';
import {Subscriber} from 'rxjs/Subscriber';
import {CoatingsDisplayGroup} from '../modify-order/coating/coating.component';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {UtilsService} from './utils.service';

@Injectable()
export class LensCatalogBusinessService {
  // Arrays of Coating objects to populate the drop down lists' options
  public arCoatings: Coating[];
  arCoatingsDisplay: CoatingsDisplayGroup[];
  scratchCoatings: Coating[];
  glassCoatings: Coating[];
  mirrorSkiCoatings: Coating[];
  uvCoatings: Coating[];
  tintColors: LensFeature[];
  tintTypes: LensFeature[];
  edges: LensFeature[];
  bevel: LensFeature[];
  miscellaneous: Map<string, Coating>;
  dataLoaded: boolean;
  mirrorSkiCoatingsMap: Map<string, Coating>;
  mirrorSkiCoatingsDisplay: CoatingsDisplayGroup[];
  uvCoatingsMap: Map<string, Coating>;
  glassCoatingsMap: Map<string, Coating>;
  scratchCoatingsMap: Map<string, Coating>;
  arCoatingsMap: Map<string, Coating>;

  constructor(
    private lensCatalogApiService: LensCatalogApiService
  ) {
  }

  initialDataLoad() {
    forkJoin(
      this.getArCoatingTypes(),
      this.getScratchCoatings(),
      this.getUvCoatings(),
      this.getMirrorSkiCoatings(),
      this.getGlassCoatings(),
      this.getTintColors(),
      this.getTintTypes(),
      this.getEdges(),
      this.getBevel(),
      this.getMiscellaneous()
    )
      .subscribe(([dataAr, dataScratch, dataUv, dataMir, dataGlass, dataTintColors, dataTintTypes, dataEdges, dataBevel, dataMisc]) => {
        this.arCoatings = dataAr;
        this.arCoatingsDisplay = this.buildArCoatingsDisplayGroupArray(dataAr);
        this.scratchCoatings = dataScratch;
        this.uvCoatings = dataUv;
        this.mirrorSkiCoatings = dataMir;
        this.mirrorSkiCoatingsDisplay = this.buildMirrorSkiCoatingsDisplayGroupArray(dataMir);
        this.glassCoatings = dataGlass;
        this.tintColors = dataTintColors;
        this.tintTypes = dataTintTypes;
        this.edges = dataEdges;
        this.bevel = dataBevel;
        this.miscellaneous = dataMisc;
        console.log('AR Coating types loaded...', dataAr);
        console.log('Scratch Coating types loaded...', dataScratch);
        console.log('UV Coating types loaded...', dataUv);
        console.log('MirSki Coating types loaded...', dataMir);
        console.log('Glass Coating types loaded...', dataGlass);
        console.log('Tint Colors loaded...', dataTintColors);
        console.log('Tint Types loaded...', dataTintTypes);
        console.log('Edges loaded...', dataEdges);
        console.log('Bevel loaded...', dataBevel);
        console.log('Miscellaneous loaded...', dataMisc);
        console.log('LensCat data loaded');
        this.mirrorSkiCoatingsMap = this.convertCoatingsArrayToMap(this.mirrorSkiCoatings);
        this.uvCoatingsMap = this.convertCoatingsArrayToMap(this.uvCoatings);
        this.glassCoatingsMap = this.convertCoatingsArrayToMap(this.glassCoatings);
        this.scratchCoatingsMap = this.convertCoatingsArrayToMap(this.scratchCoatings);
        this.arCoatingsMap = this.convertCoatingsArrayToMap(this.arCoatings);
        this.dataLoaded = true;
      }, (error) => {
        this.arCoatings = null;
        this.scratchCoatings = null;
        this.glassCoatings = null;
        this.mirrorSkiCoatings = null;
        this.uvCoatings = null;
        this.tintColors = null;
        this.tintTypes = null;
        this.edges = null;
        this.bevel = null;
        this.miscellaneous = null;
        this.mirrorSkiCoatingsMap = null;
        this.uvCoatingsMap = null;
        this.glassCoatingsMap = null;
        this.scratchCoatingsMap = null;
        this.arCoatingsMap = null;
        // console.error('Did not retrieve coatings section');
      });
  }

  // take the returned ar coating data and make an object that the dropdown can display including <optgroup> groupings.
  buildMirrorSkiCoatingsDisplayGroupArray(mirrorSkiData): CoatingsDisplayGroup[] {
    // sort by category and description
    mirrorSkiData.sort(this.sortCoatings);

    // create a map of data, keyed off coatingGroup (e.g. 'QP', 'QR')
    const coatingMap = new Map();
    mirrorSkiData.forEach(coating => {
      if (!coatingMap.get(coating.coatingGroup)) {
        const coatingArray: Coating[] = [];
        coatingArray.push(coating);
        coatingMap.set(coating.coatingGroup, coatingArray);
      } else {
        const coatingArray: Coating[] = coatingMap.get(coating.coatingGroup);
        coatingArray.push(coating);
      }
    });

    // take the map and create the object array we pass back
    const mirrorSkiCoatingsDisplayGroups: CoatingsDisplayGroup[] = [];

    coatingMap.forEach((items, key: string) => {
      const group: CoatingsDisplayGroup = {group: '', items: []};
      group.group = this.updateMirrorSkiGroupDescription(key);
      group.items = this.getItemsInLegacyFormat(items);
      // insert one blank item at end of each coating group as a separator.
      group.items.push(this.getNewEmptyCoatingReferenceItem());
      mirrorSkiCoatingsDisplayGroups.push(group);
    });
    return mirrorSkiCoatingsDisplayGroups;
  }

  // Rearrange Items to conform with Legacy format
  getItemsInLegacyFormat(items: any) {

    const result: any = [];
    let count = 0;
    let flash = false;
    let mirror = false;
    let gradient = false;
    let other = false;
    let double = true;
    while (count < 5) {
      if (count === 1) {
        double = false;
        flash = true;
      } else if (count === 2) {
        flash = false;
        mirror = true;
      } else if (count === 3) {
        mirror = false;
        gradient = true;
      } else if (count === 4) {
        gradient = false;
        other = true;
      }
      items.forEach(i => {
        if (double && i.description.toUpperCase().indexOf('DOUBLE') !== -1) {
            result.push(i);
        } else if (flash && i.description.toUpperCase().indexOf('FLASH') !== -1) {
            result.push(i);
        } else if (mirror && i.description.toUpperCase().indexOf('MIRROR') !== -1) {
          if (i.description.toUpperCase().indexOf('GRADIENT') === -1) {
            result.push(i);
          }
        } else if (gradient && i.description.toUpperCase().indexOf('GRADIENT') !== -1) {
          if (i.description.toUpperCase().indexOf('DOUBLE') === -1) {
            result.push(i);
          }
        } else if (other) {
          if (i.description.toUpperCase().indexOf('GRADIENT') === -1 &&
              i.description.toUpperCase().indexOf('MIRROR') === -1   &&
              i.description.toUpperCase().indexOf('FLASH') === -1    &&
              i.description.toUpperCase().indexOf('DOUBLE') === -1) {
            i.description = '*' + i.description;
            result.push(i);
          }
        }
      });
      count++;
    }
    return result;
  }

  // take the returned ar coating data and make an object that the dropdown can display including <optgroup> groupings.
  buildArCoatingsDisplayGroupArray(arData): CoatingsDisplayGroup[] {
    // sort by category and description
    arData.sort(this.sortCoatings);

    // create a map of data, keyed off coatingGroup (e.g. 'AR-C', 'AR-A')
    const coatingMap = new Map();
    arData.forEach(coating => {
      if (!coatingMap.get(coating.coatingGroup)) {
        const coatingArray: Coating[] = [];
        coatingArray.push(coating);
        coatingMap.set(coating.coatingGroup, coatingArray);
      } else {
        const coatingArray: Coating[] = coatingMap.get(coating.coatingGroup);
        coatingArray.push(coating);
      }
    });

    // take the map and create the object array we pass back
    const arCoatingsDisplayGroups: CoatingsDisplayGroup[] = [];
    coatingMap.forEach((items, key: string) => {
      const group: CoatingsDisplayGroup = {group: '', items: []};
      group.group = key ? key.replace(/AR-/g, 'AR Coating ') : '';
      items.forEach(item => {
        group.items.push(item);
      });
      // insert one blank item at end of each coating group as a separator.
      group.items.push(this.getNewEmptyCoatingReferenceItem());
      arCoatingsDisplayGroups.push(group);
    });
    return arCoatingsDisplayGroups;
  }

  // We retrieve data from the lens catalog api, whose items are in the form { externalId: number, name: string }
  // and we want it in the from { id: string, name: string }. For example:
  // { externalId: 1, name: 'Single Vision' } --> { id: 'SINGLE_VISION', name: 'Single Vision' }
  getVisionTypes() {
    return this.lensCatalogApiService.getVisionTypes()
      .map(result => this.convertVisionTypesResult(result));
  }

  getTintColors() {
    return this.lensCatalogApiService.getTintColors()
      .map(result => this.convertTintColorLensFeatureResult(result));
  }

  getTintTypes() {
    return this.lensCatalogApiService.getTintTypes()
      .map(result => this.convertTintTypeLensFeatureResult(result));
  }

  getEdges() {
    return this.lensCatalogApiService.getEdges()
      .map(result => this.convertEdgeLensFeatureResult(result));
  }

  getMiscellaneous() {
    return this.lensCatalogApiService.getMiscellaneous()
      .map(result => this.convertMiscCoatingResult(result));
  }

  getBevel() {
    return this.lensCatalogApiService.getBevel()
      .map(result => this.convertBevelLensFeatureResult(result));
  }

  getMaterialTypes() {
    return this.lensCatalogApiService.getMaterialTypes()
      .map(result => this.convertMaterialTypesResult(result));
  }

  getArCoatingTypes() {
    return this.lensCatalogApiService.getArCoatings()
      .map(result => this.convertArCoatingsResult(result));
  }

  getScratchCoatings() {
    return this.lensCatalogApiService.getScratchCoatings()
      .map(result => this.convertCoatingsResult(result));
  }

  getUvCoatings() {
    return this.lensCatalogApiService.getUvCoatings()
      .map(result => this.convertCoatingsResult(result));
  }

  getMirrorSkiCoatings() {
    return this.lensCatalogApiService.getMirrorSkiCoatings()
      .map(result => this.convertMirrorSkiCoatingsResult(result));
  }

  getGlassCoatings() {
    return this.lensCatalogApiService.getGlassCoatings()
      .map(result => this.convertCoatingsResult(result));
  }

  getLensDetail(labOrder: LabOrder) {
    console.log('getLensDetail');
    return this.lensCatalogApiService.getLensDetail(labOrder.lens.spectacleLensId, labOrder.serviceDate)
      .map(result => this.convertLensDetailResult(result));
  }

  getLensDetailWithOrderNo(labOrder: LabOrder) {
    console.log('getLensDetailWithOrderNo');
    return this.lensCatalogApiService.getLensDetail(labOrder.lens.spectacleLensId, labOrder.serviceDate)
      .map(result => this.convertLensDetailResultWithOrderNo(result, labOrder));
  }

  getLenses(labOrder: LabOrder): Observable<LensStyle[]> {
    const channelName = this.getChannelName(labOrder);
    const visionType = this.mapLabVisionCodeToLensCatalogVisionCode(labOrder.lens.visionTypeCode);
    const materialType = this.mapLabMaterialCodeToLensCatalogMaterial(labOrder.lens.materialCode);
    const dateOfService = labOrder.serviceDate;

    if (channelName && visionType && materialType && dateOfService) {
      return new Observable<LensStyle[]>((observer: Subscriber<LensStyle[]>) => {
        this.lensCatalogApiService.getLenses(channelName, visionType, materialType, dateOfService).subscribe((lensesResponse) => {
            if (lensesResponse && lensesResponse.items && lensesResponse.items.length > 0) {
              observer.next(lensesResponse.items);
            } else {
              observer.next([]);
            }
            observer.complete();
          },
          (error) => {
            console.error('Error calling lens catalog service.');
            return observer.error(error);
          });
      });
    } else {
      return Observable.of([]);
    }
  }

  // The rules are simply for determining channelName.
  // For Private orders, Lens list should map to the ELAB channel.
  // For VSP orders, Lens list should map to the ECLAIM channel.
  // The channelName should NOT determined by finishingType 'In-Office Uncut' or 'Lab Finishing'
  //
  getChannelName(labOrder: LabOrder): string {
    let channelName: string;
    if (UtilsService.isPrivateOrder(labOrder)) {
      channelName = 'ELAB';
    } else {
      channelName = 'ECLAIM';
    }
    console.log('returning channelName=' + channelName + ' for lab order');
    return channelName;
  }

  private sortCoatings(a, b) {
    // We want 'None' to sort to the top.
    if (a.description.trim().toLowerCase() === 'none') {
      return 1;
    }

    if ((a.coatingGroup + a.description) < (b.coatingGroup + b.description)) {
      return -1;
    }
    if ((a.coatingGroup + a.description) > (b.coatingGroup + b.description)) {
      return 1;
    }
    return 0;
  }

  // convert from LensCoatingResponse to LensFeature
  convertBevelLensFeatureResult(result: LensCoatingTypeResponse): LensFeature[] {
    const retVal: LensFeature[] = this.convertLensFeatureResult(LensFeatureType.BEVEL, result);

    // Add 'None' to beginning and 'Other' to the end
    retVal.unshift(this.getLensFeatureItem(' ', '', ''));
    retVal.push(this.getLensFeatureItem('5', 'Other', 'OTHER_BEVEL'));
    return retVal;
  }

  // convert from LensCoatingTypeResponse to Map<string, Coating>
  convertMiscCoatingResult(result: LensCoatingTypeResponse): Map<string, Coating> {
    if (!result) {
      return new Map<string, Coating>();
    }

    const retVal: Map<string, Coating> = new Map<string, Coating>();

    const things: LensCatalogCoatingReferenceType[] = result.items;
    for (let i = 0; i < things.length; i++) {
      const item: LensCatalogCoatingReferenceType = things[i];

      const formularyCode = [];
      if (item.vspFormularyAssessment && item.vspFormularyAssessment.formularyEnhancements &&
        item.vspFormularyAssessment.formularyEnhancements.length > 0) {
        item.vspFormularyAssessment.formularyEnhancements.forEach(formularyEnhancement => {
          formularyCode.push(formularyEnhancement.code);
        });
      }

      if (formularyCode.length === 0) {
        formularyCode.push('');
      }

      // Get the lab service description like 'Press-On Prism'
      const description: string = item.labServiceDescription;
      // Get the lab service id like 'Z6'
      const labServiceId: string = item.labServiceId;

      // insert into coating 'SH', 'Press-On Prism', 'Z6'
      const newItem = new Coating(formularyCode, description, labServiceId);
      retVal.set(newItem.getPreferredCode(), newItem);
    }
    return retVal;
  }

  // convert from LensCoatingResponse to LensFeature
  convertEdgeLensFeatureResult(result: LensCoatingTypeResponse): LensFeature[] {
    const retVal: LensFeature[] = this.convertLensFeatureResult(LensFeatureType.EDGE, result);

    // Add 'None' to beginning and 'Other' to the end
    retVal.unshift(this.getLensFeatureItem(' ', '', ''));
    retVal.push(this.getLensFeatureItem('', 'Other', 'OTHER'));
    return retVal;
  }

  // sort lens features alpha.
  private sortLensFeatures(a, b) {

    if (a.description < b.description) {
      return -1;
    }
    if (a.description > b.description) {
      return 1;
    }
    return 0;
  }

  // convert from LensCoatingResponse to LensFeature
  convertTintTypeLensFeatureResult(result: LensCoatingTypeResponse): LensFeature[] {
    const retVal: LensFeature[] = this.convertLensFeatureResult(LensFeatureType.TINT_TYPE, result);
    retVal.sort(this.sortLensFeatures);
    // Add 'None' to beginning
    retVal.unshift(this.getLensFeatureItem(' ', '', ''));

    return retVal;
  }

  // convert from LensCoatingResponse to LensFeature
  convertTintColorLensFeatureResult(result: LensCoatingTypeResponse): LensFeature[] {
    const retVal: LensFeature[] = this.convertLensFeatureResult(LensFeatureType.TINT_COLOR, result);
    retVal.sort(this.sortLensFeatures);
    // Add 'None' to beginning and 'Other' to the end
    retVal.unshift(this.getLensFeatureItem(' ', '', ''));

    return retVal;
  }

  // convert from LensCoatingResponse to LensFeature
  convertLensFeatureResult(type: string, result: LensCoatingTypeResponse): LensFeature[] {
    if (!result) {
      return new Array();
    }
    const retVal: LensFeature[] = [];

    const things: LensCatalogCoatingReferenceType[] = result.items;
    for (let i = 0; i < things.length; i++) {
      const item: LensCatalogCoatingReferenceType = things[i];
      const catalogApiEnum = this.getCatalogApiEnum(type, item.labServiceId);
      const newItem: LensFeature = new LensFeature(item.labServiceId, item.labServiceDescription, catalogApiEnum);
      retVal.push(newItem);
    }
    return retVal;
  }

  convertVisionTypesResult(result: LensVisionTypeResponse): ReferenceListItem[] {
    // console.log("===", result);
    if (!result) {
      return new Array();
    }

    const retVal: ReferenceListItem[] = [];
    retVal.push(this.getNewEmptyReferenceListItem());

    const things: LensMaterialReferenceType[] = result.items;
    for (let i = 0; i < things.length; i++) {
      const item: LensMaterialReferenceType = things[i];
      const newItem: ReferenceListItem = {id: this.convertVisionTypesExternalIdToId(item.externalId), name: item.name};
      retVal.push(newItem);
    }

    return retVal;
  }

  // convert the Coating array to a Coating Map, keyed by hashValue
  // so on a save we update all Coating fields in the database.
  convertCoatingsArrayToMap(coatingArray: Coating[]): Map<string, Coating> {

    const retVal: Map<string, Coating> = new Map<string, Coating>();

    coatingArray.forEach(coating => {
      retVal.set(coating.hashValue, coating);
    });
    return retVal;
  }

  convertMirrorSkiCoatingsResult(result: LensCoatingTypeResponse): Coating[] {

    if (!result) {
      return new Array();
    }

    const retVal: Coating[] = [];
    // Add 'other' mirror/ski coating
    retVal.push(new Coating(['QR'], 'Other', '', 'QR'));

    result.items.forEach(item => {
      const formularyCode: string[] = []; // like 'QP'
      let coatingGroup: string;
      if (item.vspFormularyAssessment && item.vspFormularyAssessment.formularyEnhancements &&
        item.vspFormularyAssessment.formularyEnhancements.length > 0) {

        coatingGroup = item.vspFormularyAssessment.formularyEnhancements[0].code;
        item.vspFormularyAssessment.formularyEnhancements.forEach(formularyEnhancement => {
          formularyCode.push(formularyEnhancement.code);
        });
      }

      if (formularyCode.length === 0) {
        formularyCode.push(' ');
      }

      // Get the lab service description, usually a color like tope.
      const description: string = item.labServiceDescription;

      // get mirror/ski lab service id, like 'M3'
      const labServiceId: string = item.labServiceId;
      const newItem = new Coating(formularyCode, description, labServiceId, coatingGroup);
      retVal.push(newItem);
    });

    return retVal;
  }

  convertArCoatingsResult(result: LensCoatingTypeResponse): Coating[] {

    if (!result) {
      return new Array();
    }

    const retVal: Coating[] = [];

    result.items.forEach(item => {

      const formularyCodes: string[] = [];
      let coatingGroup: string;
      if (item.vspFormularyAssessment) {

        if (item.vspFormularyAssessment.formularyEnhancements) {
          item.vspFormularyAssessment.formularyEnhancements.forEach(formularyEnhancement => {
            formularyCodes.push(formularyEnhancement.code);
          });
        }

        // default to blank string to match None ' '
        if (formularyCodes.length === 0) {
          formularyCodes.push(' ');
        }

        // get the AR Coating group 'AR-A','AR-B','AR-C','AR-D'.
        if (item.vspFormularyAssessment.arAssessmentCategory) {
          coatingGroup = item.vspFormularyAssessment.arAssessmentCategory;
        }
      }

      const labServiceId: string = item.labServiceId;
      const newItem = new Coating(formularyCodes, item.brandDescription, labServiceId, coatingGroup);
      retVal.push(newItem);
    });

    return retVal;
  }

  convertCoatingsResult(result: LensCoatingTypeResponse): Coating[] {

    if (!result) {
      return new Array();
    }

    const retVal: Coating[] = [];
    retVal.push(this.getNewEmptyCoatingReferenceItem());

    result.items.forEach(item => {
      // default to blank string to match None ' '
      const formularyCodes: string[] = [];
      // the coating "code" values are formulary enhancements on the vspFormularyAssessment object as returned from Lens Catalog
      if (item.vspFormularyAssessment && item.vspFormularyAssessment.formularyEnhancements
        && item.vspFormularyAssessment.formularyEnhancements.length > 0) {

        item.vspFormularyAssessment.formularyEnhancements.forEach(formularyEnhancement => {
          formularyCodes.push(formularyEnhancement.code);
        });
      }

      // from the data returned from Lens Catalog, favor brandDescription over labServiceDescription
      const description: string = item.brandDescription ? item.brandDescription : item.labServiceDescription;

      const labServiceId: string = item.labServiceId;
      const newItem = new Coating(formularyCodes, description, labServiceId);
      retVal.push(newItem);
    });

    return retVal;
  }

  convertMaterialTypesResult(result: LensMaterialTypeResponse): ReferenceListItem[] {
    if (!result) {
      return new Array();
    }

    const retVal: ReferenceListItem[] = [];
    retVal.push(this.getNewEmptyReferenceListItem());

    const things: LensMaterialReferenceType[] = result.items;
    for (let i = 0; i < things.length; i++) {
      const item: LensMaterialReferenceType = things[i];
      const newItem: ReferenceListItem = {id: this.convertMaterialTypesExternalIdToId(item.externalId), name: item.name};
      retVal.push(newItem);
    }

    return retVal;
  }

  convertLensDetailResult(result: LensCatalogReferenceType): LensCatalogReferenceType {
    if (!result) {
      return null;
    }
    return {externalId: result.externalId, legacyLensColor: result.legacyLensColor,
      spectacleLensFormularyAssessment: result.spectacleLensFormularyAssessment};

  }
  convertLensDetailResultWithOrderNo(result: LCatRefWithOrderNumberType, labOrder: LabOrder): LCatRefWithOrderNumberType {
    if (!result) {
      return null;
    }
    return {externalId: result.externalId, legacyLensColor: result.legacyLensColor, labOrderNumber: labOrder.labOrderNumber,
      productDescription: result.productDescription};

  }

  private convertVisionTypesExternalIdToId(externalId: number): string {
    let id = '';
    switch (externalId) {
      case 0 :
        id = 'SINGLE_VISION';
        break;
      case 1 :
        id = 'BIFOCAL';
        break;
      case 2 :
        id = 'TRIFOCAL';
        break;
      case 3 :
        id = 'QUAD';
        break;
      case 4 :
        id = 'DOUBLE_VISION';
        break;
      case 5 :
        id = 'PROGRESSIVE';
        break;
      case 6 :
        id = 'NEAR_VARIABLE_FOCUS';
        break;
      case 7 :
        id = 'LENTICULAR_SINGLE_VISION';
        break;
      case 8 :
        id = 'LENTICULAR_BIFOCAL';
        break;

      default :
        console.log('Unknown submission.');
        id = 'UNKNOWN';
        break;
    }
    return id;
  }

  private convertMaterialTypesExternalIdToId(externalId: number): string {
    let id = '';
    switch (externalId) {
      case 0 :
        id = 'GLASS';
        break;
      case 1 :
        id = 'POLYCARBONATE';
        break;
      case 2 :
        id = 'PLASTIC';
        break;
      case 3 :
        id = 'PLASTIC_HIGH_INDEX';
        break;
      case 4 :
        id = 'GLASS_HIGH_INDEX';
        break;
      case 5 :
        id = 'PLASTIC_MID_INDEX';
        break;
      case 6 :
        id = 'TRIVEX';
        break;

      default :
        console.log('Unknown submission.');
        id = 'UNKNOWN';
        break;
    }
    return id;
  }

  // This gives us our unselect option in the dropdown
  private getNewEmptyReferenceListItem() {
    return {'id': '', 'name': ''};
  }

  private getNewEmptyCoatingReferenceItem(): Coating {
    return new Coating([' '], '');
  }

  private getLensFeatureItem(code: string, description: string, apiEnum: string): LensFeature {
    return new LensFeature(code, description, apiEnum);
  }

  private mapLabVisionCodeToLensCatalogVisionCode(visionTypeCode: string): string {
    let lenscatalogVisionCode = '';
    if (visionTypeCode !== null && visionTypeCode !== '') {
      switch (visionTypeCode) {
        case 'SINGLE_VISION' :
          lenscatalogVisionCode = 'Single Vision';
          break;
        case 'BIFOCAL' :
          lenscatalogVisionCode = 'Bifocal';
          break;
        case 'TRIFOCAL' :
          lenscatalogVisionCode = 'Trifocal';
          break;
        case 'QUAD' :
          lenscatalogVisionCode = 'Quad';
          break;
        case 'DOUBLE_VISION' :
          lenscatalogVisionCode = 'Double';
          break;
        case 'PROGRESSIVE' :
          lenscatalogVisionCode = 'Progressive';
          break;
        case 'NEAR_VARIABLE_FOCUS' :
          lenscatalogVisionCode = 'Near Variable Focus';
          break;
        case 'LENTICULAR_SINGLE_VISION' :
          lenscatalogVisionCode = 'Lenticular Single Vision';
          break;
        case 'LENTICULAR_BIFOCAL' :
          lenscatalogVisionCode = 'Lenticular Bifocal';
          break;

        default :
          console.log('Unknown submission.');
          lenscatalogVisionCode = 'UNKNOWN';
          break;
      }
    }

    return lenscatalogVisionCode;
  }

  private mapLabMaterialCodeToLensCatalogMaterial(materialCode: string): string {
    let lensCatalogMaterialCode = '';
    if (materialCode !== null && materialCode !== '') {
      switch (materialCode) {
        case 'GLASS' :
          lensCatalogMaterialCode = 'Glass';
          break;
        case 'POLYCARBONATE' :
          lensCatalogMaterialCode = 'Polycarbonate';
          break;
        case 'PLASTIC' :
          lensCatalogMaterialCode = 'Plastic';
          break;
        case 'PLASTIC_HIGH_INDEX' :
          lensCatalogMaterialCode = 'Plastic Hi Index';
          break;
        case 'GLASS_HIGH_INDEX' :
          lensCatalogMaterialCode = 'Glass Hi Index';
          break;
        case 'PLASTIC_MID_INDEX' :
          lensCatalogMaterialCode = 'Plastic Mid Index';
          break;
        case 'TRIVEX' :
          lensCatalogMaterialCode = 'Trivex';
          break;

        default :
          console.log('Unknown submission.');
          lensCatalogMaterialCode = 'UNKNOWN';
          break;
      }
    }

    return lensCatalogMaterialCode;
  }

  // update the group code from LensCatalog API to something matching legacy application.
  private updateMirrorSkiGroupDescription(groupCode: string): string {
    let groupDescription = '';

    if (groupCode) {
      if (groupCode === 'QP') {
        groupDescription = 'Gold/Silver Mirror Coating';
      } else if (groupCode === 'QR') {
        groupDescription = 'Ski/Other Color Mirror Coating';
      }
    }
    return groupDescription;
  }

  // takes in the lensFeatureType and routes it to the correct labFeature method
  getCatalogApiEnum(lensFeatureType: string, code: string): string {
    switch (lensFeatureType) {
      case LensFeatureType.BEVEL:
        return this.getBevelCatalogApiEnum(code);
      case LensFeatureType.EDGE:
        return this.getEdgeCatalogApiEnum(code);
      case LensFeatureType.TINT_TYPE:
        return this.getTintTypeCatalogApiEnum(code);
      case LensFeatureType.TINT_COLOR:
        return this.getTintColorCatalogApiEnum(code);
      default:
        return '';
    }
  }

  // translates between LensCatalogApi code ('E4') and what
  // Enum value is required by LabOrderAPI ('EDGE_COATING')
  getEdgeCatalogApiEnum(code: string): string {
    if (!code) {
      return '';
    }
    let catalogApiEnum = '';
    this.translateEdgeApiEnum().forEach(edge => {
      if (edge.labServiceId === code) {
        catalogApiEnum = edge.labOrderApiEnum;
      }
    });
    return catalogApiEnum;
  }

  // translates between LensCatalogApi code ('E1') and what
  // Enum value is required by LabOrderAPI ('HIGH_LUSTER_EDGE_POLISH')
  getBevelCatalogApiEnum(code: string): string {
    if (!code) {
      return '';
    }
    let catalogApiEnum = '';
    this.translateBevelApiEnum().forEach(bevel => {
      if (bevel.labServiceId === code) {
        catalogApiEnum = bevel.labOrderApiEnum;
      }
    });
    return catalogApiEnum;
  }

  // translates between LensCatalogApi code ('E4') and what
  // Enum value is required by LabOrderAPI ('EDGE_COATING')
  getTintTypeCatalogApiEnum(code: string): string {
    if (!code) {
      return '';
    }
    let catalogApiEnum = '';
    this.translateTintTypeApiEnum().forEach(tintType => {
      if (tintType.labServiceId === code) {
        catalogApiEnum = tintType.labOrderApiEnum;
      }
    });
    return catalogApiEnum;
  }

  // translates between LensCatalogApi code ('E4') and what
  // Enum value is required by LabOrderAPI ('EDGE_COATING')
  getTintColorCatalogApiEnum(code: string): string {
    if (!code) {
      return '';
    }
    let catalogApiEnum = '';
    this.translateTintColorApiEnum().forEach(tintColor => {
      if (tintColor.labServiceId === code) {

        catalogApiEnum = tintColor.labOrderApiEnum;
      }
    });
    return catalogApiEnum;
  }

  private translateEdgeApiEnum() {
    return [
      {
        'labOrderApiEnum': 'EDGE_COATING',
        'labServiceId': 'E4'
      },
      {
        'labOrderApiEnum': 'ROLL_AND_HIGH_LUSTER_FINISH',
        'labServiceId': 'E2'
      },
      {
        'labOrderApiEnum': 'FACETTED_LENSES',
        'labServiceId': 'E5'
      },
      {
        'labOrderApiEnum': 'STANDARD_FINISH',
        'labServiceId': 'E6'
      },
      {
        'labOrderApiEnum': 'ROLL_AND_STANDARD_FINISH',
        'labServiceId': 'E7'
      },
      {
        'labOrderApiEnum': 'HIGH_LUSTER_EDGE_POLISH',
        'labServiceId': 'E1'
      },
      {
        'labOrderApiEnum': 'PAINTED_GROOVE',
        'labServiceId': 'E3'
      }
    ];
  }

  private translateBevelApiEnum() {
    return [
      {
        'labOrderApiEnum': 'ONE_THIRD_TO_TWO_THIRD',
        'labServiceId': '1'
      },
      {
        'labOrderApiEnum': 'FIFTY_FIFTY',
        'labServiceId': '2'
      },
      {
        'labOrderApiEnum': 'LAYOVER',
        'labServiceId': '3'
      },
      {
        'labOrderApiEnum': 'RIMLESS_OR_FLAT',
        'labServiceId': '4'
      },
      {
        'labOrderApiEnum': 'OTHER_BEVEL',
        'labServiceId': '5'
      },
      {
        'labOrderApiEnum': 'HIDE_A_BEVEL',
        'labServiceId': '6'
      }
    ];
  }

  private translateTintColorApiEnum() {
    return [
      {
        'labOrderApiEnum': 'BROWN_1',
        'labServiceId': 'BROW'

      },
      {
        'labOrderApiEnum': 'BROWN_2',
        'labServiceId': 'BROW2'
      },
      {
        'labOrderApiEnum': 'BROWN_3',
        'labServiceId': 'BROW3'
      },
      {
        'labOrderApiEnum': 'CRUX_A',
        'labServiceId': 'CRUXA'
      },
      {
        'labOrderApiEnum': 'CRUX_AX',
        'labServiceId': 'CRUXX'
      },
      {
        'labOrderApiEnum': 'G15',
        'labServiceId': 'G15'
      },
      {
        'labOrderApiEnum': 'GRAY_1',
        'labServiceId': 'GRA1'
      },
      {
        'labOrderApiEnum': 'GRAY_2',
        'labServiceId': 'GRA2'
      },
      {
        'labOrderApiEnum': 'GRAY_3',
        'labServiceId': 'GRA3'
      },
      {
        'labOrderApiEnum': 'OTHER',
        'labServiceId': 'OTHER'
      },
      {
        'labOrderApiEnum': 'PINK_1',
        'labServiceId': 'PINK1'
      },
      {
        'labOrderApiEnum': 'PINK_2',
        'labServiceId': 'PINK2'
      },
      {
        'labOrderApiEnum': 'PINK_3',
        'labServiceId': 'PINK3'
      },
      {
        'labOrderApiEnum': 'ROSE_1',
        'labServiceId': 'ROSE1'
      },
      {
        'labOrderApiEnum': 'ROSE_2',
        'labServiceId': 'ROSE2'
      },
      {
        'labOrderApiEnum': 'ROSE_3',
        'labServiceId': 'ROSE3'
      }
    ];
  }

  private translateTintTypeApiEnum() {
    return [
      {
        'labOrderApiEnum': 'DR_TINT_IN_OFFICE_GRADIENT_COLOR',
        'labServiceId': 'T6'
      },
      {
        'labOrderApiEnum': 'DR_TINT_IN_OFFICE_SOLID_NOT_PINK',
        'labServiceId': 'T5'
      },
      {
        'labOrderApiEnum': 'PLASTIC_DYES_SOLID_PINK',
        'labServiceId': 'T1'
      },
      {
        'labOrderApiEnum': 'PLASTIC_DYES_SOLID_NOT_PINK',
        'labServiceId': 'T2'
      },
      {
        'labOrderApiEnum': 'PLASTIC_DYES_GRADIENT_COLOR',
        'labServiceId': 'T3'
      },
      {
        'labOrderApiEnum': 'DR_TINT_IN_OFFICE_SOLID_PINK',
        'labServiceId': 'T4'
      }
    ];
  }

}



// WEBPACK FOOTER //
// /home/jenkins-agent/workspace/ng-labportal-sandbox-build/src/app/shared/lens-catalog-business.service.ts